/* SASANE PDP interactions — no dependencies, no framework.
   Handles the sleep-position tabs and the support-map hotspots. */

function initTabs(root) {
  const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
  const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
  const diagram = root.querySelector('[data-sasane-diagram]');
  if (tabs.length === 0) return;

  function select(index, { focus = false } = {}) {
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
    if (diagram) {
      diagram.setAttribute('data-active-zones', tabs[index].dataset.zones || '');
    }
    if (focus) tabs[index].focus();
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(i));
    tab.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      select((i + step + tabs.length) % tabs.length, { focus: true });
    });
  });

  select(0);
}

function initHotspots(root) {
  const spots = Array.from(root.querySelectorAll('[data-sasane-hotspot]'));
  const details = Array.from(root.querySelectorAll('[data-sasane-hotspot-detail]'));
  if (spots.length === 0) return;

  function select(id) {
    spots.forEach((spot) => {
      spot.setAttribute('aria-expanded', String(spot.dataset.sasaneHotspot === id));
    });
    details.forEach((detail) => {
      detail.hidden = detail.dataset.sasaneHotspotDetail !== id;
    });
  }

  spots.forEach((spot) => {
    spot.addEventListener('click', () => select(spot.dataset.sasaneHotspot));
  });

  if (spots[0]) select(spots[0].dataset.sasaneHotspot);
}

/**
 * Drives the theme's own variant picker from the bundle cards, so price, media,
 * add-to-cart and the sticky bar keep using Horizon's logic. Without JS the native
 * picker stays visible and usable.
 */
function initBundle(root) {
  if (root.dataset.sasaneBundleInitialized === 'true') return;
  if (root.dataset.sasaneBundleMode === 'quantity') {
    initQuantityBundle(root);
    return;
  }
  const inputs = Array.from(root.querySelectorAll('input[data-sasane-variant-id]'));
  if (inputs.length === 0) return;

  const picker = document.querySelector('variant-picker');
  if (!picker) return;
  root.dataset.sasaneBundleInitialized = 'true';
  picker.hidden = true;
  picker.setAttribute('aria-hidden', 'true');

  const nativeFor = (variantId) => picker.querySelector(`input[data-variant-id="${variantId}"]`);

  function updateFinalCtas(input) {
    if (!input) return;
    document.querySelectorAll('[data-sasane-final-cta]').forEach((cta) => {
      const variantField = cta.querySelector('[data-sasane-final-variant]');
      const price = cta.querySelector('[data-sasane-final-price]');
      if (variantField) {
        variantField.value = input.dataset.sasaneVariantId;
      }
      if (price && input.dataset.sasanePrice) price.textContent = input.dataset.sasanePrice;
    });
  }

  function syncProductForms(input) {
    if (!input) return;
    const variantId = input.dataset.sasaneVariantId;
    document
      .querySelectorAll('product-form-component input[name="id"][ref="variantId"]')
      .forEach((field) => {
        if (field.value !== variantId) field.value = variantId;
      });
    updateFinalCtas(input);
  }

  function syncCheckedVariant() {
    syncProductForms(inputs.find((input) => input.checked));
  }

  for (const input of inputs) {
    input.addEventListener('change', () => {
      if (!input.checked) return;
      syncProductForms(input);
      const target = nativeFor(input.dataset.sasaneVariantId);
      if (target && !target.checked) target.click();
      queueMicrotask(() => syncProductForms(input));
    });
  }

  picker.addEventListener('change', () => {
    const checked = picker.querySelector('input[data-variant-id]:checked');
    if (!checked) return;
    const mine = inputs.find((i) => i.dataset.sasaneVariantId === checked.dataset.variantId);
    if (!mine) return;
    if (!mine.checked) mine.checked = true;
    syncProductForms(mine);
  });

  const productSection = picker.closest('.shopify-section');
  productSection?.addEventListener('shopify:product:select', (event) => {
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(syncCheckedVariant, syncCheckedVariant);
    } else {
      queueMicrotask(syncCheckedVariant);
    }
  });

  const productForms = Array.from(document.querySelectorAll('product-form-component'));
  productForms.forEach((component) => {
    component.addEventListener('submit', syncCheckedVariant, { capture: true });
    const form = component.querySelector('form');
    form?.addEventListener('formdata', (event) => {
      const checked = inputs.find((input) => input.checked);
      if (checked) event.formData.set('id', checked.dataset.sasaneVariantId);
    });
  });

  syncCheckedVariant();
  requestAnimationFrame(syncCheckedVariant);
  customElements.whenDefined('product-form-component').then(() => {
    syncCheckedVariant();
    requestAnimationFrame(syncCheckedVariant);
  });
  window.addEventListener('load', syncCheckedVariant, { once: true });
}

/**
 * Quantity options for single-variant products: the checked option decides how many units
 * every add-to-cart form of this product sends. Forms of other products on the page
 * (recommendations, quick add) are left alone.
 */
function initQuantityBundle(root) {
  const inputs = Array.from(root.querySelectorAll('input[data-sasane-quantity]'));
  if (inputs.length === 0) return;
  root.dataset.sasaneBundleInitialized = 'true';

  const productId = root.dataset.productId;
  const checkedInput = () => inputs.find((input) => input.checked);
  if (!checkedInput()) {
    const firstEnabled = inputs.find((input) => !input.disabled);
    if (firstEnabled) firstEnabled.checked = true;
  }

  const productForms = () =>
    Array.from(document.querySelectorAll('product-form-component')).filter(
      (component) => !productId || component.dataset.productId === productId
    );

  function apply() {
    const input = checkedInput();
    if (!input) return;
    const quantity = input.dataset.sasaneQuantity;
    productForms().forEach((component) => {
      component.dataset.quantityDefault = quantity;
      component.querySelectorAll('input[name="quantity"]').forEach((field) => {
        field.value = quantity;
      });
    });
    document.querySelectorAll('[data-sasane-final-cta] [data-sasane-final-price]').forEach((price) => {
      if (input.dataset.sasanePrice) price.textContent = input.dataset.sasanePrice;
    });
  }

  inputs.forEach((input) => input.addEventListener('change', apply));

  productForms().forEach((component) => {
    component.addEventListener('submit', apply, { capture: true });
    component.querySelector('form')?.addEventListener('formdata', (event) => {
      const input = checkedInput();
      if (input) event.formData.set('quantity', input.dataset.sasaneQuantity);
    });
  });

  apply();
  customElements.whenDefined('quantity-selector-component').then(() => requestAnimationFrame(apply));
  window.addEventListener('load', apply, { once: true });
}

function init(scope = document) {
  scope.querySelectorAll('[data-sasane-tabs]').forEach(initTabs);
  scope.querySelectorAll('[data-sasane-hotspots]').forEach(initHotspots);
  scope.querySelectorAll('[data-sasane-bundle]').forEach(initBundle);
}

init();

// Theme editor re-renders a section's markup without reloading the page.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
